import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import ConfigurableDrawer from "./ConfigurableDrawer";
import ConvertToCustomerModal from "./ConvertToCustomerModal";
import PortalAccessManager from "./PortalAccessManager";
import QualifyPanel from "./QualifyPanel";
import SmsComposer from "./SmsComposer";
import WhatsappComposer from "./WhatsappComposer";
import { WebphoneDialog } from "./WebphoneDialog";
import { Btn, eur, dt, Badge, AccountActivityPreview } from "./common";
import { toast } from "sonner";
import {
  Mail, Phone, ExternalLink, Target, User2, CircleDot,
  Copy, LifeBuoy, CheckSquare, UserPlus, MessageSquare, Building2, UserCheck,
  Send, StickyNote, Pencil, PhoneCall, MessageCircle,
} from "lucide-react";

const FIELD_DEFS = [
  { key: "nif", label: "NIF", render: (a) => a.nif || "-" },
  { key: "setor", label: "Setor", render: (a) => a.setor || "-" },
  { key: "website", label: "Website", render: (a) => a.website ? <a href={a.website} target="_blank" rel="noreferrer" className="text-blue hover:underline">{a.website}</a> : "-" },
  { key: "divida_aberta", label: "Divida em aberto", render: (a) => a.divida_aberta ? eur(a.divida_aberta) : "-" },
  { key: "limite_credito", label: "Limite de credito", render: (a) => a.limite_credito ? eur(a.limite_credito) : "sem limite" },
  { key: "criado_em", label: "Criada em", render: (a) => a.criado_em ? dt(a.criado_em) : "-" },
  { key: "origem", label: "Origem", render: (a) => a.origem || "-" },
  { key: "responsavel", label: "Responsavel", render: (a) => a.responsavel_nome || a.responsavel_email || "-" },
  { key: "morada", label: "Morada", render: (a) => a.morada || "-" },
  { key: "email", label: "Email geral", render: (a) => a.email ? <a href={`mailto:${a.email}`} className="text-blue hover:underline">{a.email}</a> : "-" },
  { key: "telefone", label: "Telefone", render: (a) => a.telefone || "-" },
];

const DEFAULT_VISIBLE = Object.fromEntries(FIELD_DEFS.slice(0, 6).map((f) => [f.key, true]));

const DEFAULT_ACTIONS = ["email", "ligar", "ligar_letscall", "sms", "whatsapp", "nova_nota", "criar_tarefa", "criar_ticket", "criar_oportunidade", "novo_contacto", "copiar_nif"];
const DEFAULT_ACTIONS_VISIBLE = { email: true, ligar: true, ligar_letscall: false, sms: true, whatsapp: true, nova_nota: true, criar_tarefa: true, criar_ticket: true, criar_oportunidade: true, novo_contacto: false, copiar_nif: true };

function makeActionDefs(nav, openSms, openWhatsapp, onLigar) {
  return [
    { key: "email", label: "Enviar email", icon: Mail, enabled: (a) => !!a?.id,
      run: (a) => nav(`/app/crm/contas/${a.id}?tab=emails&compose=1&assunto=${encodeURIComponent(a.nome || "")}`) },
    { key: "ligar", label: "Ligar (LetsCall)", icon: PhoneCall, enabled: (a) => !!(a?.telefone),
      run: (a) => onLigar(a.telefone || "") },
    { key: "ligar_letscall", label: "Click-to-Call (LetsCall)", icon: PhoneCall, enabled: (a) => !!(a?.telefone),
      run: (a) => onLigar(a.telefone || "") },
    { key: "sms", label: "Enviar SMS", icon: Send, enabled: (a) => !!a?.id,
      run: (a) => openSms(a) },
    { key: "whatsapp", label: "Enviar WhatsApp", icon: MessageCircle, enabled: (a) => !!(a?.telefone),
      run: (a) => openWhatsapp(a) },
    { key: "criar_tarefa", label: "Criar tarefa", icon: CheckSquare, enabled: (a) => !!a?.id,
      run: (a) => nav(`/app/tarefas?account_id=${a.id}&nova=1&titulo=${encodeURIComponent(`Follow-up ${a.nome || ""}`)}`) },
    { key: "criar_oportunidade", label: "Criar oportunidade", icon: Target, enabled: (a) => !!a?.id,
      run: (a) => nav(`/app/crm/contas/${a.id}?tab=oportunidades&nova_oportunidade=1`) },
    { key: "criar_ticket", label: "Criar ticket", icon: LifeBuoy, enabled: (a) => !!a?.nome,
      run: (a) => nav(`/app/suporte?account_id=${a.id || ""}&nova=1&organizacao_nome=${encodeURIComponent(a.nome || "")}`) },
    { key: "novo_contacto", label: "Novo contacto", icon: UserPlus, enabled: (a) => !!a?.id,
      run: (a) => nav(`/app/crm/contas/${a.id}?tab=contactos&novo_contacto=1`) },
    { key: "nova_nota", label: "Nota / actividade", icon: MessageSquare, enabled: (a) => !!a?.id,
      run: (a) => nav(`/app/crm/atividades?account_id=${a.id}&nova=1&tipo=nota&assunto=${encodeURIComponent(`Nota ${a.nome || ""}`)}`) },
    { key: "copiar_nif", label: "Copiar NIF", icon: Copy, enabled: (a) => !!a?.nif,
      run: async (a) => {
        if (!a.nif) return toast.message("Sem NIF.");
        try { await navigator.clipboard.writeText(a.nif); toast.success(`NIF ${a.nif} copiado.`); }
        catch { toast.error("Nao foi possivel copiar."); }
      } },
  ];
}

export default function AccountDrawer({ accountId, open, onClose }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [acc, setAcc] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneDialogNum, setPhoneDialogNum] = useState("");

  const reload = () => {
    if (!accountId) return;
    setLoading(true);
    Promise.all([
      api.get(`/crm/accounts/${accountId}`),
      api.get(`/crm/contacts`, { params: { account_id: accountId } }).catch(() => ({ data: [] })),
      api.get(`/crm/deals`, { params: { account_id: accountId } }).catch(() => ({ data: [] })),
    ]).then(([a, c, d]) => {
      setAcc(a.data);
      setContacts(c.data || []);
      setDeals(d.data || []);
    }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !accountId) return;
    let cancel = false;
    setLoading(true); setAcc(null); setContacts([]); setDeals([]);
    Promise.all([
      api.get(`/crm/accounts/${accountId}`),
      api.get(`/crm/contacts`, { params: { account_id: accountId } }).catch(() => ({ data: [] })),
      api.get(`/crm/deals`, { params: { account_id: accountId } }).catch(() => ({ data: [] })),
    ]).then(([a, c, d]) => {
      if (cancel) return;
      setAcc(a.data);
      setContacts(c.data || []);
      setDeals(d.data || []);
    }).catch((e) => !cancel && toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [accountId, open]);

  const abrir = () => { onClose && onClose(); nav(`/app/crm/contas/${accountId}`); };
  const placeholders = acc ? {
    id: acc.id || "", nif: acc.nif || "", nome: acc.nome || "",
    email: acc.email || "", telefone: acc.telefone || "", website: acc.website || "",
    morada: acc.morada || "", setor: acc.setor || "", origem: acc.origem || "",
    responsavel_email: acc.responsavel_email || "", responsavel_nome: acc.responsavel_nome || "",
    tipo: acc.tipo || "",
  } : {};

  const extraSections = [
    { key: "badges", label: "Badges", defaultVisible: true,
      render: (a) => (
        <div className="space-y-2 -mt-3">
          <div className="flex flex-wrap items-center gap-2">
            <Badge value={a.tipo || "-"} />
            {a.credito_estado && (
              <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border ${a.credito_estado === "vermelho" ? "bg-red-50 text-red-700 border-red-200" : a.credito_estado === "ambar" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                <CircleDot size={11} /> credito {a.credito_estado}
              </span>
            )}
          </div>
          <Btn onClick={abrir} testid="account-drawer-open-full-top" className="w-full justify-center">
            <ExternalLink size={13} /> Abrir ficha completa
          </Btn>
          <Btn variant="ghost" onClick={() => {
            onClose && onClose();
            nav(`/app/crm/contas?edit_id=${accountId}`);
          }} testid="account-drawer-edit" className="w-full justify-center">
            <Pencil size={13} /> Editar ficha
          </Btn>
          <Btn variant="ghost" onClick={() => {
            onClose && onClose();
            nav(`/app/crm/atividades?account_id=${accountId}&nova=1`);
          }} testid="account-drawer-new-activity" className="w-full justify-center">
            <StickyNote size={13} /> Registar atividade / nota
          </Btn>
          {(a.criado_por_email || a.origem) && (
            <div className="mt-1 text-[10px] text-slate-500 space-y-0.5" data-testid="account-drawer-creator">
              {a.origem && <div>Origem: <span className="font-semibold text-navy">{a.origem}</span></div>}
              {a.criado_por_email && a.origem === "manual" && (
                <div>Criado por <span className="font-semibold text-navy">{a.criado_por_email}</span>{a.criado_em ? ` a ${(a.criado_em || "").slice(0, 10)}` : ""}</div>
              )}
            </div>
          )}
        </div>
      ) },
    { key: "contacts", label: `Contactos (${contacts.length})`, defaultVisible: true,
      render: () => (
        <>
          {contacts.length === 0 && <div className="text-[12px] text-slate-400 italic">Sem contactos.</div>}
          <div className="space-y-1.5">
            {contacts.slice(0, 5).map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onClose && onClose(); nav(`/app/crm/contas/${accountId}?tab=contactos&contact_id=${c.id}`); }}
                data-testid={`drawer-contact-${c.id}`}
                className="w-full flex items-center gap-2 bg-slate-50 rounded-md px-2.5 py-1.5 hover:bg-blue/10 hover:ring-1 hover:ring-blue/30 transition-colors text-left"
                title="Abrir ficha do contacto"
              >
                <div className="w-6 h-6 rounded-full bg-blue/15 text-blue text-[10px] font-bold flex items-center justify-center shrink-0">
                  {(c.nome || "?").split(" ").slice(0, 2).map((x) => x[0] || "").join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-navy truncate">{c.nome}</div>
                  <div className="text-[11px] text-slate-500 truncate flex items-center gap-2">
                    {c.email && <><Mail size={10} /> {c.email}</>}
                    {c.telefone && <><Phone size={10} /> {c.telefone}</>}
                    {c.departamento && <span className="text-[10px] uppercase tracking-wider rounded px-1 border border-slate-200 bg-white">{c.departamento}</span>}
                  </div>
                </div>
                <ExternalLink size={11} className="text-slate-400 shrink-0" />
              </button>
            ))}
            {contacts.length > 5 && <div className="text-[11px] text-slate-500">+ {contacts.length - 5} contactos - abra a ficha completa para ver todos</div>}
          </div>
        </>
      ) },
    { key: "deals", label: `Oportunidades (${deals.length})`, defaultVisible: true,
      render: () => (
        <>
          {deals.length === 0 && <div className="text-[12px] text-slate-400 italic">Sem oportunidades.</div>}
          <div className="space-y-1.5">
            {deals.slice(0, 5).map((d) => (
              <div key={d.id} data-testid={`drawer-deal-${d.id}`} className="rounded-md border border-slate-200 bg-white px-3 py-2 hover:border-blue/50 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[13px] font-semibold text-navy truncate flex items-center gap-1.5"><Target size={12} /> {d.nome}</div>
                  <Badge value={d.estado || d.etapa || "-"} />
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                  <span>{d.valor ? eur(d.valor) : "-"}</span>
                  {d.prob != null && <span>- {d.prob}% prob.</span>}
                  {d.responsavel && <span className="flex items-center gap-1"><User2 size={10} /> {d.responsavel}</span>}
                </div>
              </div>
            ))}
            {deals.length > 5 && <div className="text-[11px] text-slate-500">+ {deals.length - 5} oportunidades</div>}
          </div>
        </>
      ) },
    { key: "portal", label: "Portal do Cliente", defaultVisible: true,
      render: (a) => <PortalAccessManager account={a} /> },
    { key: "qualify", label: "Qualificacao", defaultVisible: true,
      render: (a) => <QualifyPanel account={a} /> },
    { key: "activity", label: "Atividade recente", defaultVisible: true,
      render: (a) => <AccountActivityPreview accountId={a.id} /> },
  ];

  return (
    <>
    <ConfigurableDrawer
      open={open} onClose={onClose}
      overline="CRM"
      title={acc?.nome || (loading ? "A carregar..." : "Conta")}
      testid="account-drawer"
      uiPrefKey="account_drawer" uiPrefVersion="v3"
      fieldDefs={FIELD_DEFS}
      actionDefs={makeActionDefs(nav, () => setSmsOpen(true), () => setWaOpen(true), (tel) => { setPhoneDialogNum(tel); setPhoneDialogOpen(true); })}
      defaultActions={DEFAULT_ACTIONS}
      defaultActionsVisible={DEFAULT_ACTIONS_VISIBLE}
      extraSections={extraSections}
      entity={acc}
      entityLoading={loading && !acc}
      placeholders={placeholders}
      footer={
        <>
          <Btn variant="ghost" onClick={onClose} testid="account-drawer-close-btn">Fechar</Btn>
          {acc && acc.tipo !== "cliente" && (
            <Btn variant="ghost" onClick={() => setShowConvert(true)} testid="account-convert-btn">
              <UserCheck size={14} /> Converter em cliente
            </Btn>
          )}
          <Btn onClick={abrir} testid="account-drawer-open-full"><ExternalLink size={14} /> Abrir ficha completa</Btn>
        </>
      }
    />
    <ConvertToCustomerModal open={showConvert} onClose={() => setShowConvert(false)} account={acc} onDone={reload} />
    <WebphoneDialog open={phoneDialogOpen} number={phoneDialogNum} onClose={() => setPhoneDialogOpen(false)} />
    <SmsComposer
      open={smsOpen}
      onClose={() => setSmsOpen(false)}
      contactos={contacts}
      accountId={accountId || ""}
      nome={acc?.nome || ""}
    />
    <WhatsappComposer
      open={waOpen}
      onClose={() => setWaOpen(false)}
      numero={acc?.telefone || ""}
      accountId={accountId || ""}
      nome={acc?.nome || ""}
    />
    </>
  );
}

// Expor os defaults dos campos visiveis para caso alguem queira reset externamente
AccountDrawer.DEFAULT_VISIBLE = DEFAULT_VISIBLE;
